import type { AppError } from '@core/api/normalizeError'
import { formatToInternational } from '@shared/utils'
import { formatMarketTokenPrice } from '../utils/tradingFormatUtils'

// investor-service secondary-market error codes → the spec copy the investor
// reads (contract v1, "Error codes"). One table for every surface — a 4xx
// from a write and an `errors[]` entry on a quote resolve to the same line.
// `[X]` / `[floor]` / `[ceiling]` placeholders are filled from the error's
// `details` (writes) or from the quote / listing figures (quotes).

export const TRADING_ERROR = {
  PROPERTY_NOT_TRADABLE: 'MARKETPLACE_PROPERTY_NOT_TRADABLE',
  TRADING_PAUSED: 'MARKETPLACE_TRADING_PAUSED',
  LISTING_NOT_FOUND: 'MARKETPLACE_LISTING_NOT_FOUND',
  LISTING_UNAVAILABLE: 'MARKETPLACE_LISTING_UNAVAILABLE',
  ORDER_BELOW_MIN_VALUE: 'MARKETPLACE_ORDER_BELOW_MIN_VALUE',
  ORDER_EXCEEDS_AVAILABLE: 'MARKETPLACE_ORDER_EXCEEDS_AVAILABLE',
  ORDER_EXCEEDS_OWNERSHIP_CAP: 'MARKETPLACE_ORDER_EXCEEDS_OWNERSHIP_CAP',
  ORDER_OWN_LISTING: 'MARKETPLACE_ORDER_OWN_LISTING',
  ORDER_INSUFFICIENT_BALANCE: 'MARKETPLACE_ORDER_INSUFFICIENT_BALANCE',
  BUYER_NOT_ELIGIBLE: 'MARKETPLACE_BUYER_NOT_ELIGIBLE',
  ORDER_NOT_FOUND: 'MARKETPLACE_ORDER_NOT_FOUND',
  ORDER_NOT_CANCELLABLE: 'MARKETPLACE_ORDER_NOT_CANCELLABLE',
  SELLER_NOT_ELIGIBLE: 'MARKETPLACE_SELLER_NOT_ELIGIBLE',
  LISTING_LOCK_IN_ACTIVE: 'MARKETPLACE_LISTING_LOCK_IN_ACTIVE',
  LISTING_EXCEEDS_SELLABLE: 'MARKETPLACE_LISTING_EXCEEDS_SELLABLE',
  LISTING_BELOW_MIN_VALUE: 'MARKETPLACE_LISTING_BELOW_MIN_VALUE',
  LISTING_PRICE_OUT_OF_BAND: 'MARKETPLACE_LISTING_PRICE_OUT_OF_BAND',
  LISTING_NO_SELLABLE_TOKENS: 'MARKETPLACE_LISTING_NO_SELLABLE_TOKENS',
  VALUATION_UNAVAILABLE: 'MARKETPLACE_VALUATION_UNAVAILABLE',
  LISTING_NOT_CANCELLABLE: 'MARKETPLACE_LISTING_NOT_CANCELLABLE',
  LISTING_RENEWAL_WINDOW_CLOSED: 'MARKETPLACE_LISTING_RENEWAL_WINDOW_CLOSED',
  LISTING_RENEWAL_OUT_OF_BAND: 'MARKETPLACE_LISTING_RENEWAL_OUT_OF_BAND',
  TERMS_NOT_ACCEPTED: 'MARKETPLACE_TERMS_NOT_ACCEPTED',
  INVALID_IDEMPOTENCY_KEY: 'MARKETPLACE_INVALID_IDEMPOTENCY_KEY',
  // Sell-overview eligibility reasons owned by other services.
  ACCOUNT_NOT_READY: 'ACCOUNT_NOT_READY_FOR_TRANSACTION',
  BANK_NOT_VERIFIED: 'BANK_ACCOUNT_NOT_VERIFIED',
} as const

/** Values the copy placeholders read — from `details` or the caller's figures. */
export interface TradingErrorValues {
  tokensAvailable?: number
  maxTokens?: number
  /** Per-investor ownership cap, % of the property. */
  capPct?: number
  sellable?: number
  bandFloor?: number
  bandCeiling?: number
}

const tokens = (value: number | undefined): string =>
  value === undefined ? 'the available' : formatToInternational(value)

const price = (value: number | undefined): string =>
  value === undefined ? '-' : formatMarketTokenPrice(value)

const capLimit = (value: number | undefined): string =>
  value === undefined ? 'share' : `${value}%`

type CopyBuilder = (values: TradingErrorValues) => string

const LISTING_GONE = 'This listing is no longer available.'

const TRADING_ERROR_COPY: Record<string, CopyBuilder> = {
  [TRADING_ERROR.PROPERTY_NOT_TRADABLE]: () => "This property isn't available on the Marketplace.",
  [TRADING_ERROR.TRADING_PAUSED]: () => 'Trading is currently paused for this property.',
  [TRADING_ERROR.LISTING_NOT_FOUND]: () => LISTING_GONE,
  [TRADING_ERROR.LISTING_UNAVAILABLE]: () => LISTING_GONE,
  [TRADING_ERROR.ORDER_BELOW_MIN_VALUE]: () => 'The minimum order value is AED 1,000.',
  [TRADING_ERROR.ORDER_EXCEEDS_AVAILABLE]: (values) =>
    `Only ${tokens(values.tokensAvailable)} tokens are available in this listing.`,
  // No headroom left at all reads differently from "up to N".
  [TRADING_ERROR.ORDER_EXCEEDS_OWNERSHIP_CAP]: (values) =>
    values.maxTokens === 0
      ? `You already hold the maximum ${capLimit(values.capPct)} allowed for this property, so you can't buy more tokens.`
      : `You can buy up to ${tokens(values.maxTokens)} tokens in this property under the ownership limit.`,
  [TRADING_ERROR.ORDER_OWN_LISTING]: () => "You can't buy from your own listing.",
  [TRADING_ERROR.ORDER_INSUFFICIENT_BALANCE]: () =>
    'Insufficient wallet balance. Please add funds to continue.',
  [TRADING_ERROR.BUYER_NOT_ELIGIBLE]: () => 'Please complete the required steps before buying.',
  [TRADING_ERROR.ORDER_NOT_CANCELLABLE]: () => 'This order can no longer be cancelled.',
  [TRADING_ERROR.SELLER_NOT_ELIGIBLE]: () => 'Please complete the required steps before listing.',
  [TRADING_ERROR.LISTING_LOCK_IN_ACTIVE]: () =>
    'These tokens are within the 3-month lock-in period and cannot be listed yet.',
  [TRADING_ERROR.LISTING_EXCEEDS_SELLABLE]: (values) =>
    `You have ${tokens(values.sellable)} tokens available to list.`,
  [TRADING_ERROR.LISTING_BELOW_MIN_VALUE]: () => 'The minimum listing value is AED 1,000.',
  [TRADING_ERROR.LISTING_PRICE_OUT_OF_BAND]: (values) =>
    `Set a price within ${price(values.bandFloor)}–${price(values.bandCeiling)} per token.`,
  [TRADING_ERROR.LISTING_NO_SELLABLE_TOKENS]: () =>
    'You have no tokens available to list right now.',
  [TRADING_ERROR.VALUATION_UNAVAILABLE]: () =>
    'Valuation data is unavailable; listing is blocked until it is.',
  [TRADING_ERROR.LISTING_NOT_CANCELLABLE]: () => "This listing can't be cancelled.",
  [TRADING_ERROR.LISTING_RENEWAL_WINDOW_CLOSED]: () =>
    "This listing has expired and can't be renewed. You can relist it.",
  [TRADING_ERROR.LISTING_RENEWAL_OUT_OF_BAND]: () =>
    'This price is now outside the allowed range. Please cancel and relist at a compliant price.',
  [TRADING_ERROR.ACCOUNT_NOT_READY]: () => 'Please complete KYC verification before listing.',
  [TRADING_ERROR.BANK_NOT_VERIFIED]: () => 'Add and verify a bank account before listing.',
}

/** Spec copy for a code, or null when the code has none (fall back to the caller's). */
export const tradingErrorCopy = (
  code: string | undefined,
  values: TradingErrorValues = {},
): string | null => {
  const build = code ? TRADING_ERROR_COPY[code] : undefined
  return build ? build(values) : null
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const toDetailNumber = (value: unknown): number | undefined => {
  if (typeof value !== 'number' && typeof value !== 'string') return undefined
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

// `AppError.details` is the raw response body; the placeholder values sit
// under its own `details` key ({ success:false, errorCode, message, details }).
const valuesFromError = (error: AppError): TradingErrorValues => {
  const body = isRecord(error.details) ? error.details : {}
  const details = isRecord(body.details) ? body.details : {}
  return {
    tokensAvailable: toDetailNumber(details.tokensAvailable),
    maxTokens: toDetailNumber(details.maxTokens),
    sellable: toDetailNumber(details.sellable),
    bandFloor: toDetailNumber(details.bandFloor),
    bandCeiling: toDetailNumber(details.bandCeiling),
  }
}

/** The line to show for a failed write: spec copy for known codes, else `fallback`. */
export const resolveTradingError = (error: unknown, fallback: string): string => {
  if (!isRecord(error)) return fallback
  const appError: AppError = {
    message: typeof error.message === 'string' ? error.message : fallback,
    errorCode: typeof error.errorCode === 'string' ? error.errorCode : undefined,
    details: error.details,
  }
  return tradingErrorCopy(appError.errorCode, valuesFromError(appError)) ?? fallback
}

/** The error code on a thrown AppError, if any. */
export const tradingErrorCode = (error: unknown): string | undefined =>
  isRecord(error) && typeof error.errorCode === 'string' ? error.errorCode : undefined
